import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Activity, Zap, FileSignature, Home, Users, Star,
    TrendingUp, MessageSquare, CheckCircle2, Euro, Eye
} from 'lucide-react';

/**
 * PulseLiveFeed - Feed d'activite temps reel de l'agence
 *
 * Affiche un flux d'evenements recents (nouveau mandat, offre, CR visite,
 * signature, note vendeur...) avec animation subtile. Effet "salle de trading
 * immo" qui accroche le dirigeant.
 *
 * Version 1 : mock d'evenements pour demo visuelle.
 * Version 2 : branchement WebSocket ou polling /api/events/recent
 */

// Event templates - in real usage these come from API
const EVENT_TEMPLATES = [
    { type: 'mandate_new', icon: FileSignature, color: 'text-[#681793]', bg: 'bg-[#681793]/10', border: 'border-[#681793]/30', label: 'Nouveau mandat' },
    { type: 'mandate_signed', icon: CheckCircle2, color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/30', label: 'Mandat signé' },
    { type: 'visit_report', icon: Eye, color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/30', label: 'CR visite' },
    { type: 'offer_received', icon: Euro, color: 'text-[#FF914D]', bg: 'bg-[#FF914D]/10', border: 'border-[#FF914D]/30', label: 'Offre reçue' },
    { type: 'seller_rating', icon: Star, color: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', label: 'Note vendeur' },
    { type: 'estimation', icon: TrendingUp, color: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/30', label: 'Estimation' },
    { type: 'message', icon: MessageSquare, color: 'text-indigo-400', bg: 'bg-indigo-500/10', border: 'border-indigo-500/30', label: 'Message' },
    { type: 'milestone', icon: Zap, color: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/30', label: 'Événement' },
];

// Generate realistic mock events from mandates + team data
function generateEvents(mandates = [], team = [], count = 8) {
    const events = [];
    const now = new Date();

    const agents = team.filter(t => t.role === 'agent' || t.role === 'agent_commercial' || t.role === 'admin');

    const addresses = mandates.map(m => m.property?.address || m.address).filter(Boolean);
    const sampleAddresses = addresses.length > 0 ? addresses : [
        'Rue de la Paix, 75001',
        'Avenue Victor Hugo, 69006',
        'Bd de la Liberté, 33000',
        'Place Bellecour, 69002',
        'Cours Gambetta, 13001',
    ];

    const sampleAgents = agents.length > 0
        ? agents.map(a => `${a.first_name || ''} ${a.last_name?.[0] || ''}.`.trim())
        : ['Sarah L.', 'Thomas B.', 'Pierre M.', 'Julie D.'];

    const eventFactories = {
        mandate_new: () => ({ detail: `${sampleAgents[Math.floor(Math.random() * sampleAgents.length)]} · ${sampleAddresses[Math.floor(Math.random() * sampleAddresses.length)]}` }),
        mandate_signed: () => ({ detail: `Exclusif 12 mois · ${sampleAddresses[Math.floor(Math.random() * sampleAddresses.length)]}` }),
        visit_report: () => ({ detail: `${Math.floor(Math.random() * 4) + 1}/5 · Avis ${['très positif', 'positif', 'réservé', 'enthousiaste'][Math.floor(Math.random() * 4)]}` }),
        offer_received: () => ({ detail: `${Math.floor(Math.random() * 400 + 200)}k€ · ${sampleAddresses[Math.floor(Math.random() * sampleAddresses.length)]}` }),
        seller_rating: () => ({ detail: `${Math.floor(Math.random() * 2) + 4}/5 étoiles · ${sampleAgents[Math.floor(Math.random() * sampleAgents.length)]}` }),
        estimation: () => ({ detail: `${sampleAgents[Math.floor(Math.random() * sampleAgents.length)]} · ${Math.floor(Math.random() * 400 + 180)}k€` }),
        message: () => ({ detail: `Vendeur → ${sampleAgents[Math.floor(Math.random() * sampleAgents.length)]}` }),
        milestone: () => ({ detail: `${Math.floor(Math.random() * 15 + 5)} mandats ce mois` }),
    };

    for (let i = 0; i < count; i++) {
        const template = EVENT_TEMPLATES[Math.floor(Math.random() * EVENT_TEMPLATES.length)];
        const factory = eventFactories[template.type];
        const detail = factory ? factory().detail : '';
        const minutesAgo = Math.floor(Math.random() * 180) + i * 3;
        events.push({
            id: `evt-${Date.now()}-${i}-${Math.random()}`,
            ...template,
            detail,
            timestamp: new Date(now.getTime() - minutesAgo * 60 * 1000),
        });
    }

    return events.sort((a, b) => b.timestamp - a.timestamp);
}

function formatRelative(date) {
    const diff = (new Date() - date) / 1000; // seconds
    if (diff < 60) return 'à l\'instant';
    if (diff < 3600) return `il y a ${Math.floor(diff / 60)}min`;
    if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
    return `il y a ${Math.floor(diff / 86400)}j`;
}

export function PulseLiveFeed({ mandates = [], team = [] }) {
    const [events, setEvents] = useState(() => generateEvents(mandates, team, 6));
    const [pulseCount, setPulseCount] = useState(0);

    // Simulate new event arrival every 18-28s
    useEffect(() => {
        const interval = setInterval(() => {
            const newEvent = generateEvents(mandates, team, 1)[0];
            // force "just now" timestamp
            newEvent.timestamp = new Date();
            setEvents(prev => [newEvent, ...prev].slice(0, 10));
            setPulseCount(c => c + 1);
        }, 18000 + Math.random() * 10000);
        return () => clearInterval(interval);
    }, [mandates, team]);

    const hasEvents = events.length > 0;

    return (
        <div
            className="bg-card backdrop-blur-sm rounded-2xl border border-border overflow-hidden"
            data-testid="pulse-live-feed"
        >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border">
                <div className="flex items-center gap-2">
                    <div className="relative">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center">
                            <Activity className="w-4 h-4 text-white" />
                        </div>
                        <motion.span
                            key={pulseCount}
                            className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-green-400"
                            animate={{ scale: [1, 1.8, 1], opacity: [1, 0.3, 1] }}
                            transition={{ duration: 1.2, repeat: Infinity }}
                        />
                    </div>
                    <div>
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold">Pulse agence</p>
                        <p className="text-sm font-bold text-foreground">Activité en direct</p>
                    </div>
                </div>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 text-[10px] font-bold">
                    <span className="w-1 h-1 rounded-full bg-green-400 animate-pulse" />
                    Live
                </span>
            </div>

            {/* Feed */}
            <div className="p-3 max-h-[380px] overflow-y-auto">
                {!hasEvents ? (
                    <div className="text-center py-8">
                        <Activity className="w-6 h-6 text-muted-foreground mx-auto mb-2 opacity-50" />
                        <p className="text-xs text-muted-foreground">Aucune activité pour le moment</p>
                    </div>
                ) : (
                    <AnimatePresence initial={false}>
                        <div className="space-y-1.5">
                            {events.map((evt, idx) => (
                                <motion.div
                                    key={evt.id}
                                    layout
                                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.95 }}
                                    transition={{ duration: 0.35 }}
                                    className={`flex items-start gap-2 p-2.5 rounded-xl border ${evt.border} ${evt.bg} ${idx === 0 && evt.timestamp > new Date(Date.now() - 60000) ? 'ring-1 ring-[#FF914D]/30' : ''}`}
                                >
                                    <div className={`w-7 h-7 rounded-lg ${evt.bg} border ${evt.border} flex items-center justify-center shrink-0`}>
                                        <evt.icon className={`w-3.5 h-3.5 ${evt.color}`} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between gap-2">
                                            <p className={`text-[11px] font-bold ${evt.color}`}>{evt.label}</p>
                                            <p className="text-[9px] text-muted-foreground shrink-0">{formatRelative(evt.timestamp)}</p>
                                        </div>
                                        <p className="text-[11px] text-foreground/80 truncate">{evt.detail}</p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </AnimatePresence>
                )}
            </div>

            {/* Footer */}
            <div className="px-4 py-2.5 border-t border-border bg-white/[0.02]">
                <p className="text-[10px] text-muted-foreground text-center">
                    {events.length} événement{events.length > 1 ? 's' : ''} récent{events.length > 1 ? 's' : ''} · Mise à jour automatique
                </p>
            </div>
        </div>
    );
}

export default PulseLiveFeed;
